/**
 * GateGroupSummary — compact roll-up of what an intermediate gate's
 * upstream agents produced. Shown on the Gate 1 / Gate 2 tabs (and in the
 * Job Detail rail) so the reviewer sees the whole group at a glance before
 * deciding, without opening each agent tab.
 */
import { STAGE_LABELS } from '@/types/enums';
import { summaryBitsFor, latestAttemptByStage, overallConfidence, gateConfidence } from './StageHistoryRowHelpers';
import { StatusBadge } from './StageStatusBadge';
import { ConfidenceBadge } from './ConfidenceBadge';
import { upstreamStagesFor } from './gateGroups';
import type { AgentResult } from '@/types';

interface Props {
  /** 'gate_1' | 'gate_2'. Anything else renders nothing. */
  gate: string;
  agents: AgentResult[];
}

export function GateGroupSummary({ gate, agents }: Props) {
  const stages = upstreamStagesFor(gate);
  if (stages.length === 0) return null;
  const byStage = latestAttemptByStage(agents);
  const agg = gateConfidence(gate, agents);

  return (
    <div className="rounded border bg-muted/20 p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Upstream agents ({stages.length})
        </p>
        {agg !== null && <ConfidenceBadge score={agg} label="Gate confidence" />}
      </div>
      <ul className="space-y-1.5">
        {stages.map((stage) => {
          const row = byStage.get(stage);
          const bits = row ? summaryBitsFor(stage, row.output ?? null) : [];
          const conf = row ? overallConfidence(stage, row.output ?? null) : null;
          return (
            <li key={stage} className="flex items-center gap-2 text-sm">
              <span className="font-medium">{STAGE_LABELS[stage]}</span>
              {bits.length > 0 && (
                <span className="text-xs text-muted-foreground">{bits.join(' · ')}</span>
              )}
              <span className="ml-auto flex items-center gap-2 shrink-0">
                {conf !== null && <ConfidenceBadge score={conf} label="conf" />}
                {row ? (
                  <StatusBadge result={row} />
                ) : (
                  <span className="text-xs italic text-muted-foreground">not run yet</span>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
